import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, Grid, MenuItem, Select } from '@material-ui/core';
import { calculatePointsForLengthOfStay } from '../../util/lengthOfStayUtil';

const styles = makeStyles((theme) => ({
  sectionSpacer: {
    height: 50,
    padding: '10px 0',
    marginTop: '25px',
  },
  sectionHeader: {
    fontSize: '1.125rem',
    lineHeight: '1.375rem',
    fontWeight: 600,
  },
  subSectionHeader: {
    fontSize: '1.00rem',
    lineHeight: '1.25rem',
    fontWeight: 600,
  },
  groupHeader: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 600,
  },
  rowSpacer: {
    height: 30,
  },
  textRowSpacer: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
  },
  [theme.breakpoints.down('xs')]: {
    textField: {
      fontSize: '0.75rem',
    },
    root: {
      '& .MuiFormLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  printedContent: {
    '@media print': {
      marginBottom: 10,
    },
    '@media screen': {
      display: 'none',
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  alignPrintedContent: {
    lineHeight: '25px',
    verticalAlign: 'top',
  },
  spacer: {
    marginTop: '30px',
  },
}));

export default function Scoring(props) {
  const {
    gradingGPA,
    setGradingGPA,
    gradingFinancialNeed,
    setGradingFinancialNeed,
    gradingLengthOfStay,
    setGradingLengthOfStay,
    gradingEssayVoice,
    setGradingEssayVoice,
    gradingEssayInspiration,
    setGradingEssayInspiration,
    gradingEssayLanguage,
    setGradingEssayLanguage,
    gradingEssayCompleted,
    setGradingEssayCompleted,
    gradingCitizenshipLetters,
    setGradingCitizenshipLetter,
    gradingCitizenshipActivities,
    setGradingCitizenshipActivities,
    gradingCitizenshipRoleModel,
    setGradingCitizenshipRoleModel,
    gradingCitizenshipPlanningChecklistCompletion,
    setGradingCitizenshipPlanningChecklistCompletion,
    disabled,
    senior,
    applicant_type,
    version,
  } = props;

  const classes = styles();

  const todaysDate = new Date();

  const calculatePointsForGPA = () => {
    let score = 0;
    const gpa = props.senior.gpa || 0;
    if (gpa) {
      if (version === 2) {
        if (gpa >= 3.8) {
          score = 20;
        } else if (gpa >= 3.5) {
          score = 15;
        } else if (gpa >= 3.0) {
          score = 10;
        } else {
          score = 0;
        }
      } else {
        if (gpa > 3.25) {
          score = 40;
        } else if (gpa > 2.5) {
          score = 30;
        } else if (gpa > 2) {
          score = 20;
        } else {
          score = 0;
        }
      }
      setGradingGPA(score);
      return score;
    }
    return 0;
  };

  const lengthOfStayResult = React.useMemo(
    () => calculatePointsForLengthOfStay({ version, senior, today: todaysDate }),
    [version, senior, todaysDate]
  );

  React.useEffect(() => {
    if (typeof lengthOfStayResult?.points === 'number') {
      setGradingLengthOfStay(lengthOfStayResult.points);
    }
  }, [lengthOfStayResult?.points, setGradingLengthOfStay]);

  return (
    <>
      <Grid item xs={12} align="left"></Grid>
      <Grid item xs={12} align="left" className={classes.sectionSpacer}>
        <label className={classes.sectionHeader}>Scoring</label>
      </Grid>
      {applicant_type === 'senior' && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} align="left">
            <label className={classes.subSectionHeader}>Academics ({version === 2 ? 20 : 40} points)</label>
          </Grid>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>GPA</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            {calculatePointsForGPA()}
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            <label className={classes.subSectionHeader}>Academics Score</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            {gradingGPA}
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      <Grid item xs={12} align="left">
        <label className={classes.subSectionHeader}>Financial Need (20 points)</label>
      </Grid>
      <Grid item xs={12} align="left"></Grid>
      {version === 2 && (
        <Grid item xs={12} sm={12} lg={12} align="left" style={{ marginTop: 5, marginBottom: 15 }}>
          <div className={classes.groupHeader} style={{ fontWeight: 500 }}>
            Please consider the following items from the summary sheet (estimated expenses, challenges and barriers to my plan, family will
            financially support, Pell eligible, other scholarships and awards.
          </div>
        </Grid>
      )}
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>Student's Financial Needs Covered:</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <FormControl fullWidth>
          <Select
            labelId="financial-needs-covered-label"
            id="financial-needs-covered"
            value={gradingFinancialNeed}
            onChange={(event) => setGradingFinancialNeed(event.target.value)}
            disabled={disabled}
          >
            <MenuItem value={-1} disabled>
              Select Grade
            </MenuItem>
            {version === 2
              ? [
                  <MenuItem value={20} key="pell-full">
                    Full Pell Grant Eligibility
                  </MenuItem>,
                  <MenuItem value={15} key="pell-half">
                    Half Pell Grant Eligibility
                  </MenuItem>,
                  <MenuItem value={10} key="pell-minimum">
                    Minimum Pell Grant Eligibility
                  </MenuItem>,
                  <MenuItem value={5} key="pell-none">
                    Not Pell Grant Eligible
                  </MenuItem>,
                ]
              : [
                  <MenuItem value={0} key="needs-76-100">
                    76% - 100% of student's financial needs are covered
                  </MenuItem>,
                  <MenuItem value={10} key="needs-51-75">
                    51% - 75% of student's financial needs are covered
                  </MenuItem>,
                  <MenuItem value={15} key="needs-26-50">
                    26% - 50% of student's financial needs are covered
                  </MenuItem>,
                  <MenuItem value={20} key="needs-0-25">
                    0% - 25% of student's financial needs are covered
                  </MenuItem>,
                ]}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label className={classes.subSectionHeader}>Financial Need Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {gradingFinancialNeed < 0 ? 0 : gradingFinancialNeed}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} align="left">
        <label className={classes.subSectionHeader}>Boys Town ({version === 2 ? 10 : 40} points)</label>
      </Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>Length of Stay</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        {lengthOfStayResult.points}
        {version === 2 && lengthOfStayResult.homeCampus && <span style={{ marginLeft: 8 }}>(Home Campus Youth)</span>}
        {version === 2 && !lengthOfStayResult.homeCampus && lengthOfStayResult.notEligible && (
          <span style={{ marginLeft: 8, fontWeight: 600 }}>(Not Eligible)</span>
        )}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} align="left">
        <label className={classes.groupHeader}>Essay / Personal Statement ({version === 2 ? 30 : 10} points)</label>
      </Grid>
      <Grid item xs={12} align="left"></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>Purpose and Voice:</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <FormControl fullWidth>
          <Select
            labelId="essay-voice-label"
            id="essay-voice"
            value={gradingEssayVoice}
            onChange={(event) => setGradingEssayVoice(event.target.value)}
            disabled={disabled}
          >
            <MenuItem value={-1} disabled>
              Select Grade
            </MenuItem>
            <MenuItem value={version === 2 ? 9 : 3}>
              Student’s purpose and voice is expressive with clear narrative. Student has done an outstanding job engaging the audience.
            </MenuItem>
            <MenuItem value={version === 2 ? 6 : 2}>Student’s purpose and voice is expressive with clear narrative.</MenuItem>
            <MenuItem value={version === 2 ? 3 : 1}>
              Student’s purpose and voice is inconsistent and doesn’t have a clear conception of audience.
            </MenuItem>
            <MenuItem value={0}>Student’s voice is almost completely missing and makes little attempt to engage the audience.</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>Inspiration:</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <FormControl fullWidth>
          <Select
            labelId="essay-inspiration-label"
            id="essay-inspiration"
            value={gradingEssayInspiration}
            onChange={(event) => setGradingEssayInspiration(event.target.value)}
            disabled={disabled}
          >
            <MenuItem value={-1} disabled>
              Select Grade
            </MenuItem>
            <MenuItem value={version === 2 ? 9 : 3}>
              Student tells the story in a way that makes people want to root for them. The student shows they have overcome obstacles and inspires
              others to be better.
            </MenuItem>
            <MenuItem value={version === 2 ? 6 : 2}>
              Student tells the story in a way that makes people know that they have overcome obstacles or makes others want to be better.
            </MenuItem>
            <MenuItem value={version === 2 ? 3 : 1}>
              Student is trying to tell a story of overcoming obstacles, but is inconsistent and unclear.
            </MenuItem>
            <MenuItem value={0}>Student does not share any information that appears as overcoming obstacles or as an inspiration to others.</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>Language and Word Choice:</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <FormControl fullWidth>
          <Select
            labelId="essay-word-label"
            id="essay-word"
            value={gradingEssayLanguage}
            onChange={(event) => setGradingEssayLanguage(event.target.value)}
            disabled={disabled}
          >
            <MenuItem value={-1} disabled>
              Select Grade
            </MenuItem>
            {version === 2 && [
              <MenuItem value={9} key="language-9">
                Student writes clearly and uses understandable language. If used, AI tools improve ideas and clarity while maintaining originality and
                critical thinking, and the student acknowledges the AI assistance.
              </MenuItem>,
              <MenuItem value={6} key="language-6">
                Student’s writing is mostly clear. AI tools may be used for minor improvements but demonstrate limited originality and fail to clearly
                acknowledge AI assistance.
              </MenuItem>,
              <MenuItem value={3} key="language-3">
                Student misuses several words or phrases but conveys the main idea. Student may have relied heavily on AI-generated content with
                minimal personal input or fail to show understanding of the topic.
              </MenuItem>,
              <MenuItem value={0} key="language-0">
                Student uses profanity, neglects spell check, and misuses multiple words or phrases. The submission may be entirely AI-generated with
                no personal input or acknowledgment.
              </MenuItem>,
            ]}
            {version !== 2 && [
              <MenuItem value={3} key="language-old-3">
                Student’s writing is clear and word choice is easily understood. May have used a few above average words/phrases.
              </MenuItem>,
              <MenuItem value={2} key="language-old-2">
                Student’s writing is mostly clear and understood.
              </MenuItem>,
              <MenuItem value={1} key="language-old-1">
                Student’s writing uses several words or phrases incorrectly or out of context, but the point is still apparent.
              </MenuItem>,
              <MenuItem value={0} key="language-old-0">
                Student uses profanity, has not used spell check, and has used multiple words and phrases incorrectly.
              </MenuItem>,
            ]}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>Completed</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <FormControl fullWidth>
          <Select
            labelId="essay-complete-label"
            id="essay-complete"
            value={gradingEssayCompleted}
            onChange={(event) => setGradingEssayCompleted(event.target.value)}
            disabled={disabled}
          >
            <MenuItem value={-1} disabled>
              Select Grade
            </MenuItem>
            <MenuItem value={version === 2 ? 3 : 1}>Essay was complete.</MenuItem>
            <MenuItem value={0}>Essay was incomplete.</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label className={classes.groupHeader}>Essay / Personal Statement Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {(gradingEssayVoice && gradingEssayVoice >= 0 ? gradingEssayVoice : 0) +
          (gradingEssayInspiration && gradingEssayInspiration >= 0 ? gradingEssayInspiration : 0) +
          (gradingEssayLanguage && gradingEssayLanguage >= 0 ? gradingEssayLanguage : 0) +
          (gradingEssayCompleted && gradingEssayCompleted >= 0 ? gradingEssayCompleted : 0)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} align="left">
        <label className={classes.groupHeader}>Citizenship ({version === 2 ? 20 : 10} points)</label>
      </Grid>
      <Grid item xs={12} align="left"></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginTop: 5 }}>
        <label>Recommendation Letters:</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <FormControl fullWidth>
          <Select
            labelId="citizenship-letters-label"
            id="citizenship-letters"
            value={gradingCitizenshipLetters}
            onChange={(event) => setGradingCitizenshipLetter(Number(event.target.value))}
            disabled={disabled}
          >
            <MenuItem value={-1} disabled>
              Select Grade
            </MenuItem>
            {version === 2 && [
              <MenuItem value={10} key="letters-10">
                Student scored 115-87 on recommendation rubric. 2 strong letters of recommendation from credible sources (teachers, counselors,
                mentors) that clearly support scholarship goals were received.
              </MenuItem>,
              <MenuItem value={8} key="letters-8">
                Student scored 86-59 on recommendation rubric. 2 letters were received, but one is generic or lacks strong support.
              </MenuItem>,
              <MenuItem value={4} key="letters-4">
                Student scored 58-31 on recommendation rubric. 1 strong letter of recommendation was received.
              </MenuItem>,
              <MenuItem value={2} key="letters-2">
                Student scored 30-0 on recommendation rubric. 1 letter was received, but it is generic or lacks detail.
              </MenuItem>,
              <MenuItem value={0} key="letters-0">
                Student did not submit any letters of recommendation.
              </MenuItem>,
            ]}
            {version !== 2 && [
              <MenuItem value={4} key="letters-old-4">
                Student received recommendation letters from two teachers, family teachers, assistant family teachers, or peers.
              </MenuItem>,
              <MenuItem value={2} key="letters-old-2">
                Student received one letter of recommendation.
              </MenuItem>,
              <MenuItem value={0} key="letters-old-0">
                Student did not receive any letters of recommendation.
              </MenuItem>,
            ]}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      {version !== 2 && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Extracurricular Activities:</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Select
                labelId="citizenship-activities-label"
                id="citizenship-activities"
                value={gradingCitizenshipActivities}
                onChange={(event) => setGradingCitizenshipActivities(Number(event.target.value))}
                disabled={disabled}
              >
                <MenuItem value={-1} disabled>
                  Select Grade
                </MenuItem>
                <MenuItem value={3}>
                  Student is involved in two or more extracurricular activities or holds a leadership role in one extracurricular activity and attends
                  peers' extracurricular activities and cheers for them.
                </MenuItem>
                <MenuItem value={2}>
                  Student is involved in one extracurricular activity and attends peers' extracurriculars and cheers for them.
                </MenuItem>
                <MenuItem value={1}>Student attends peers' extracurricular activities.</MenuItem>
                <MenuItem value={0}>Student not participate in extracurricular activities nor attends peers' extracurricular activities.</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Positive Role Model:</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Select
                labelId="citizenship-rolemodel-label"
                id="citizenship-rolemodel"
                value={gradingCitizenshipRoleModel}
                onChange={(event) => setGradingCitizenshipRoleModel(Number(event.target.value))}
                disabled={disabled}
              >
                <MenuItem value={-1} disabled>
                  Select Grade
                </MenuItem>
                <MenuItem value={3}>
                  Student engages in positive behaviors and encourages peers and younger youth to make good decisions. Assists within the home and
                  community without prompting.
                </MenuItem>
                <MenuItem value={2}>
                  Student engages in mostly positive behaviors and encourages peers and younger youth occasionally. Assists in home and community with
                  prompting.
                </MenuItem>
                <MenuItem value={1}>
                  Student struggles with behaviors and peer relationships but is redirectable. Assists in the home and community after more than one
                  prompt.
                </MenuItem>
                <MenuItem value={0}>
                  Student does not engage in positive behavior. Student does not assist in the home or community even when prompted.
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      {version === 2 && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Planning Checklist Completion:</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <FormControl fullWidth>
              <Select
                labelId="citizenship-planning-checklist-completion"
                id="-citizenship-planning-checklist-completion"
                value={gradingCitizenshipPlanningChecklistCompletion}
                onChange={(event) => setGradingCitizenshipPlanningChecklistCompletion(Number(event.target.value))}
                disabled={disabled}
              >
                <MenuItem value={-1} disabled>
                  Select Grade
                </MenuItem>
                <MenuItem value={10}>
                  Student completed 95-100% of Senior Checklist on time, including all required tasks (FAFSA, applications, essays, deadlines).
                </MenuItem>
                <MenuItem value={8}>Student completed 80–94% of checklist with minor delays.</MenuItem>
                <MenuItem value={4}>Student completed 60–79% of checklist; some major tasks missing.</MenuItem>
                <MenuItem value={2}>Student completed 31–59% of checklist; significant gaps.</MenuItem>
                <MenuItem value={0}>Student completed less than 30% or did not engage with checklist.</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label className={classes.subSectionHeader}>Citizenship Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {(gradingCitizenshipLetters && gradingCitizenshipLetters >= 0 ? gradingCitizenshipLetters : 0) +
          (gradingCitizenshipActivities && gradingCitizenshipActivities >= 0 ? gradingCitizenshipActivities : 0) +
          (gradingCitizenshipRoleModel && gradingCitizenshipRoleModel >= 0 ? gradingCitizenshipRoleModel : 0) +
          (gradingCitizenshipPlanningChecklistCompletion && gradingCitizenshipPlanningChecklistCompletion >= 0
            ? gradingCitizenshipPlanningChecklistCompletion
            : 0)}
      </Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label className={classes.subSectionHeader}>Boys Town Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {(gradingLengthOfStay && gradingLengthOfStay >= 0 ? gradingLengthOfStay : 0) +
          (gradingEssayVoice && gradingEssayVoice >= 0 ? gradingEssayVoice : 0) +
          (gradingEssayInspiration && gradingEssayInspiration >= 0 ? gradingEssayInspiration : 0) +
          (gradingEssayLanguage && gradingEssayLanguage >= 0 ? gradingEssayLanguage : 0) +
          (gradingEssayCompleted && gradingEssayCompleted >= 0 ? gradingEssayCompleted : 0) +
          (gradingCitizenshipLetters && gradingCitizenshipLetters >= 0 ? gradingCitizenshipLetters : 0) +
          (gradingCitizenshipActivities && gradingCitizenshipActivities >= 0 ? gradingCitizenshipActivities : 0) +
          (gradingCitizenshipRoleModel && gradingCitizenshipRoleModel >= 0 ? gradingCitizenshipRoleModel : 0) +
          (gradingCitizenshipPlanningChecklistCompletion && gradingCitizenshipPlanningChecklistCompletion >= 0
            ? gradingCitizenshipPlanningChecklistCompletion
            : 0)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label className={classes.subSectionHeader}>Overall Total Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        {(gradingGPA && gradingGPA >= 0 ? gradingGPA : 0) +
          (gradingFinancialNeed && gradingFinancialNeed >= 0 ? gradingFinancialNeed : 0) +
          (gradingLengthOfStay && gradingLengthOfStay >= 0 ? gradingLengthOfStay : 0) +
          (gradingEssayVoice && gradingEssayVoice >= 0 ? gradingEssayVoice : 0) +
          (gradingEssayInspiration && gradingEssayInspiration >= 0 ? gradingEssayInspiration : 0) +
          (gradingEssayLanguage && gradingEssayLanguage >= 0 ? gradingEssayLanguage : 0) +
          (gradingEssayCompleted && gradingEssayCompleted >= 0 ? gradingEssayCompleted : 0) +
          (gradingCitizenshipLetters && gradingCitizenshipLetters >= 0 ? gradingCitizenshipLetters : 0) +
          (gradingCitizenshipActivities && gradingCitizenshipActivities >= 0 ? gradingCitizenshipActivities : 0) +
          (gradingCitizenshipRoleModel && gradingCitizenshipRoleModel >= 0 ? gradingCitizenshipRoleModel : 0) +
          (gradingCitizenshipPlanningChecklistCompletion && gradingCitizenshipPlanningChecklistCompletion >= 0
            ? gradingCitizenshipPlanningChecklistCompletion
            : 0)}{' '}
        / {applicant_type === 'senior' ? 100 : 60}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
    </>
  );
}

Scoring.propTypes = {
  gradingGPA: PropTypes.number.isRequired,
  setGradingGPA: PropTypes.func.isRequired,
  gradingFinancialNeed: PropTypes.string.isRequired,
  setGradingFinancialNeed: PropTypes.func.isRequired,
  gradingLengthOfStay: PropTypes.number.isRequired,
  setGradingLengthOfStay: PropTypes.func.isRequired,
  gradingEssayVoice: PropTypes.string.isRequired,
  setGradingEssayVoice: PropTypes.func.isRequired,
  gradingEssayInspiration: PropTypes.string.isRequired,
  setGradingEssayInspiration: PropTypes.func.isRequired,
  gradingEssayLanguage: PropTypes.string.isRequired,
  setGradingEssayLanguage: PropTypes.func.isRequired,
  gradingEssayCompleted: PropTypes.string.isRequired,
  setGradingEssayCompleted: PropTypes.func.isRequired,
  gradingCitizenshipLetters: PropTypes.number.isRequired,
  setGradingCitizenshipLetter: PropTypes.func.isRequired,
  gradingCitizenshipActivities: PropTypes.string.isRequired,
  setGradingCitizenshipActivities: PropTypes.func.isRequired,
  gradingCitizenshipRoleModel: PropTypes.string.isRequired,
  setGradingCitizenshipRoleModel: PropTypes.func.isRequired,
  gradingCitizenshipPlanningChecklistCompletion: PropTypes.number.isRequired,
  setGradingCitizenshipPlanningChecklistCompletion: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  senior: PropTypes.object.isRequired,
  applicant_type: PropTypes.string.isRequired,
  version: PropTypes.number,
};
